import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';

import Conditional from '../../common/conditional';
import Elevation from '../../themed/instruction-modals/elevation';
import Header from '../../blocks/header';
import Input from '../../themed/input/input';
import Instruction from '../../themed/instruction-modals/instruction';
import Pagination from '../../themed/pagination';
import PDFDownload from '../../themed/pdf-download';
import TutorialCard from '../../themed/tutorial-card';
import VideoPlayer from '../../common/videoPlayer';

import { getCurrentUser } from '../../../services/authService';
import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { paginate } from '../../../utils/utilities';
import { User } from '../../../utils/models';
import { EXTERNAL_USER_GUIDE_URL, INTERNAL_USER_GUIDE_URL, USER_TYPES, tutorialConstants } from '../../../utils/constants';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import classNames from './style.module.scss';
import routes from '../../../utils/routes';

interface Tutorial {
	title: string;
	body: string;
	source: string;
	roles: string[];
}

const Tutorials: React.FC = () => {
	const [selectedTutorial, setSelectedTutorial] = useState('');
	const [tutorials, setTutorials] = useState<Tutorial[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [query, setQuery] = useState('');

	const pageSize = 6;

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const { role, userType } = getCurrentUser() as User;
	const { activeTutorial: tutorial } = useAppSelector(getTutorials);

	const changeActiveTutorial = (tutorialId: number) => {
		dispatch(setActiveTutorial(tutorialId));
	};

	const compareTutorialToQuery = (tutorial: Tutorial) => {
		return tutorial.title.toLowerCase().indexOf(query.toLowerCase()) !== -1;
	};

	const handleQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
		setQuery(e.currentTarget.value);
	};

	const navigateToHomePage = () => {
		changeActiveTutorial(tutorialConstants.INACTIVE);

		navigate(
			userType === USER_TYPES.INTERNAL
				? routes.internalUserDashboard.fullPath
				: routes.externalUserDashboard.fullPath
		);
	};

	const paginatedData = useMemo(() => {
		let filtered = tutorials;
		if (query) filtered = filtered.filter(compareTutorialToQuery);

		let sorted = _.orderBy(filtered, ['title'], ['asc']);
		let paginated = paginate(sorted, currentPage, pageSize);

		return paginated;

		// eslint-disable-next-line
	}, [tutorials, currentPage, query]);

	useEffect(() => {
		const tutorials = allTutorials.filter(({ roles }) =>
			roles.some((r) => (role as any)[r])
		);

		setTutorials(tutorials);

		// eslint-disable-next-line
	}, []);

	return (
		<React.Fragment>
			<Instruction
				isVisible={tutorial === tutorialConstants.COMPLETED}
				onProceed={navigateToHomePage}
				title="You're all caught up!"
				message="You're good to go! You can now start enjoying the application!"
			/>

			<Instruction
				isVisible={tutorial === tutorialConstants.DOWNLOAD_USER_GUIDE}
				onProceed={() => changeActiveTutorial(tutorialConstants.COMPLETED)}
				title="Download User Guide!"
				message="Click the button highlighted to download a copy of the user guide!"
			/>

			<Instruction
				isVisible={tutorial === tutorialConstants.VIEW_TUTORIALS}
				onProceed={() =>
					changeActiveTutorial(tutorialConstants.DOWNLOAD_USER_GUIDE)
				}
				title="View Tutorials!"
				message="You can view short tutorials on how to navigate the system!"
			/>

			<VideoPlayer
				sourceUrl={selectedTutorial}
				onCloseVideo={() => setSelectedTutorial('')}
			/>

			<Header subtitle="Tutorials" />

			<div className={classNames.tutorials}>
				<div className={classNames.tutorialsBanner}>
					<Input
						value={query}
						onChange={handleQueryChange}
						placeholder="Enter query e.g. View project report"
					/>

					<Elevation isElevated={tutorial === tutorialConstants.DOWNLOAD_USER_GUIDE}>
						<PDFDownload
							documentLabel="User Guide"
							documentSource={userType === USER_TYPES.INTERNAL ? INTERNAL_USER_GUIDE_URL : EXTERNAL_USER_GUIDE_URL}
						/>
					</Elevation>
				</div>

				<div className={classNames.tutorialsBody}>
					<div className="container-fluid overflow-hidden">
						<div className="row">
							{paginatedData.map((tutorial, index) => (
								<React.Fragment key={tutorial.title}>
									<div className="col-4 mb-4">
										<TutorialCard
											title={tutorial.title}
											body={tutorial.body}
											onWatchTutorial={() =>
												setSelectedTutorial(tutorial.source)
											}
										/>
									</div>

									<Conditional isVisible={(index + 1) % 3 === 0}>
										<div className="w-100"></div>
									</Conditional>
								</React.Fragment>
							))}
						</div>
					</div>

					<div className="d-flex justify-content-end">
						<Pagination
							itemsCount={tutorials.length}
							pageSize={pageSize}
							currentPage={currentPage}
							onPageChange={(page) => setCurrentPage(page)}
						/>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

const allTutorials: Tutorial[] = [
	{
		title: 'Onboarding a user',
		body: 'This video shouws you how to create a new user',
		source: 'https://res.cloudinary.com/dezg6qoig/video/upload/v1732701715/create_user_frayyd.mov',
		roles: ['manageUsers']
	},
	{
		title: 'Creating a project',
		body: 'This video shows you how to create a project as an Onshore Technical Support Officer.',
		source:
			'https://res.cloudinary.com/dvqbyseti/video/upload/v1683284287/Creating_a_project_onshore_technical_support_officer_h2mla2.mov',
		roles: ['manageProjects'],
	},
	{
		title: 'Approve project information',
		body: 'This video shows you how to validate project information as a Project Approver.',
		source:
			'https://res.cloudinary.com/dvqbyseti/video/upload/v1683283923/Accepting_a_project_report_customer_rfgsaz.mov',
		roles: ['approveReports'],
	},
	{
		title: 'Reject project information',
		body: 'This video shows you how to reject a project after creation as a Project Approver.',
		source:
			'https://res.cloudinary.com/dvqbyseti/video/upload/v1683283912/Rejecting_a_project_after_creation_xgyv7c.mov',
		roles: ['approveReports'],
	},
	{
		title: 'Prep Report Images',
		body: 'This video shows you how to prep report images as a Field Supervisor.',
		source: 'https://res.cloudinary.com/dermo1wjq/video/upload/v1715775598/markings_ouvdf9.mov',
		roles: ['manageReports', 'approveReports', 'reviewReports'],
	},
	{
		title: 'Create Project report',
		body: 'This video shows you how to create a project report as a Field Supervisor.',
		source:
			'https://res.cloudinary.com/dvqbyseti/video/upload/v1683536455/creating-a-project-report-field-supervisor_Ez7DLkP6_fmbn08.mp4',
		roles: ['manageReports'],
	},
	{
		title: 'Submit report for review',
		body: 'This video shows you how to submit a report for review as a Field Supervisor.',
		source:
			'https://res.cloudinary.com/dvqbyseti/video/upload/v1683283846/Submitting_a_report_for_review_field_supervisor_esvmto.mov',
		roles: ['manageReports'],
	},
	{
		title: 'Review project report',
		body: 'This video shows you how to review a project report as a Project Reviewer.',
		source:
			'https://res.cloudinary.com/dvqbyseti/video/upload/v1683283707/Reviewing_or_Approving_a_Project_sahvee.mov',
		roles: ['reviewReports'],
	},
	{
		title: 'Approve project report',
		body: 'This video shows you how to approve a project report as a Project Approver.',
		source:
			'https://res.cloudinary.com/dvqbyseti/video/upload/v1683283707/Reviewing_or_Approving_a_Project_sahvee.mov',
		roles: ['approveReports'],
	},
	{
		title: 'View project report',
		body: 'This video shows you how to view a generated project report.',
		source: 'https://rsidms.com/View.mov',
		roles: [
			'manageProjects',
			'manageReports',
			'approveReports',
			'reviewReports',
			'viewReports',
			'viewAssociatedReports',
		],
	},
	{
		title: 'Accept project report',
		body: 'This video shows you how to view a generated project report as the assigned Primary Contact Person.',
		source:
			'https://res.cloudinary.com/dvqbyseti/video/upload/v1683284852/Accepting_a_project_report_customer_prpgba.mov',
		roles: ['viewReports'],
	},
];

export default Tutorials;
